import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import {
  ArrowRightIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ShipmentStatusTag, WithLocalStorage } from '@/components';
import { ShipmentsGrid } from '@/components/common/shipments-grid';
import { ShipmentNumberLink } from '@/components/common/shipments-grid/ShipmentNumberLink';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { shippingMethodOptions } from '@/constants';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId, type ColDef as TypedColDef } from '@/grid/types';
import { cn } from '@/lib/utils';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import { PrimaryContext } from '@/services';
import {
  type AnimalRow,
  type AnimalShipmentConflict,
  isParcelCarrier,
  type LinkAnimalsToShipmentInput,
  useLinkAnimalsToShipment,
} from '@/services/animal-management';
import { shipmentApi } from '@/services/shipment-service/api';
import type { ShipmentDto } from '@/services/shipment-service/types';
import { useToast } from '@/services/toast';
import { useContextMenu } from '@/utils/context-menu';

import { useAnimalManagementContext } from '../context';
import { AnimalShipmentOverrideDialog } from './AnimalShipmentOverrideDialog';

const LOCKED_SHIPMENT_STATUSES = [
  ShipmentStatus.IN_PROGRESS,
  ShipmentStatus.TRANSFER_TRIP,
  ShipmentStatus.SHIPPED,
];

type PendingOverride = {
  input: LinkAnimalsToShipmentInput;
  conflicts: AnimalShipmentConflict[];
};

function useCartColumns(): TypedColDef<AnimalRow>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    const col = (key: string, fallback: string) =>
      t(`animalManagement.columns.${key}`, fallback);
    return [
      {
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 40,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'left',
        suppressHeaderMenuButton: true,
      },
      { field: 'nameTag', headerName: col('nameTag', 'Name/Tag') },
      { field: 'animalType', headerName: col('animalType', 'Animal Type') },
      { field: 'togId', headerName: col('togId', 'TOG ID') },
      { field: 'ranch', headerName: col('ranch', 'Ranch') },
      { field: 'yard', headerName: col('yard', 'Yard') },
      { field: 'housing', headerName: col('housing', 'Housing') },
    ];
  }, [t]);
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs font-medium">{label}</span>
      <div className="text-sm">{children || 'N/A'}</div>
    </div>
  );
}

function ShipmentDetailsPanel({
  shipment,
  isLocked,
  isCarrierBlocked,
}: {
  shipment: ShipmentDto | null;
  isLocked: boolean;
  isCarrierBlocked: boolean;
}) {
  const { t } = useTranslation();
  const contents = shipment
    ? [
        shipment.hasDonors && t('animalManagement.shipping.donors', 'Donors'),
        shipment.hasRecipients &&
          t('animalManagement.shipping.recipients', 'Recipients'),
        shipment.hasCalves && t('animalManagement.shipping.calves', 'Calves'),
        shipment.hasEmbryos &&
          t('animalManagement.shipping.embryos', 'Embryos'),
        shipment.hasSemen && t('animalManagement.shipping.semen', 'Semen'),
      ].filter(Boolean)
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('shipping.shipmentDetails', 'Shipment Details')}
        </CardTitle>
        {!shipment && (
          <CardDescription>
            {t('shipping.selectShipment', 'Please select a shipment')}
          </CardDescription>
        )}
      </CardHeader>
      {shipment && (
        <CardContent className="flex flex-col gap-3">
          <DetailRow
            label={t('animalManagement.shipping.shipmentNumber', 'Shipment #')}
          >
            {shipment.shipmentNumber && (
              <ShipmentNumberLink
                shipmentId={shipment.id}
                shipmentNumber={shipment.shipmentNumber}
              />
            )}
          </DetailRow>
          <DetailRow label={t('shipping.shipmentStatus', 'Shipment Status')}>
            {shipment.shipmentStatus != null && (
              <ShipmentStatusTag value={shipment.shipmentStatus} />
            )}
          </DetailRow>
          <DetailRow label={t('shipping.shipTo', 'Ship To')}>
            {shipment.shipToName}
          </DetailRow>
          <DetailRow label={t('shipping.shippingMethod', 'Shipping Method')}>
            {
              shippingMethodOptions.find(
                (option) => option.value === shipment.shippingMethod,
              )?.label
            }
          </DetailRow>
          <DetailRow
            label={t('animalManagement.shipping.contents', 'Shipping Contents')}
          >
            {contents.join(', ')}
          </DetailRow>
          {isLocked && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t(
                'animalManagement.shipping.locked',
                'Animals cannot be added to a shipment that is In Progress, a Transfer Trip, or Shipped.',
              )}
            </p>
          )}
          {isCarrierBlocked && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
              {t(
                'animalManagement.shipping.parcelCarrier',
                'UPS and FedEx cannot ship animals. Change the shipping method to add animals.',
              )}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

type ShippingIntegrationTabProps = {
  /** Called after animals are put on a shipment, so the grids can refresh. */
  onAnimalsLinked: () => void;
};

export function ShippingIntegrationTab({
  onAnimalsLinked,
}: ShippingIntegrationTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const defaultGridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { selectedAnimals, setSelectedAnimals } = useAnimalManagementContext();
  const columns = useCartColumns();
  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });
  const linkAnimals = useLinkAnimalsToShipment();

  const cartGridRef = useRef<AgGridReact>(null);
  const shipmentsGridRef = useRef<AgGridReact>(null);
  const [checkedAnimals, setCheckedAnimals] = useState<AnimalRow[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDto | null>(
    null,
  );
  const [shipmentSearchInput, setShipmentSearchInput] = useState('');
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState('');
  const [pendingOverride, setPendingOverride] =
    useState<PendingOverride | null>(null);

  const isLocked =
    selectedShipment?.shipmentStatus != null &&
    LOCKED_SHIPMENT_STATUSES.includes(selectedShipment.shipmentStatus);
  const isCarrierBlocked = isParcelCarrier(selectedShipment?.shippingMethod);

  const removeFromCart = useCallback(
    (ids: Set<string>) => {
      setSelectedAnimals(selectedAnimals.filter((a) => !ids.has(a.id)));
      setCheckedAnimals([]);
    },
    [selectedAnimals, setSelectedAnimals],
  );

  const refreshSelectedShipment = (shipmentId: string) => {
    if (selectedShipment?.id !== shipmentId || !selectedShipment.shipmentNumber)
      return;
    shipmentApi
      .getShipments(
        new URLSearchParams({
          shipmentNumber: selectedShipment.shipmentNumber,
        }),
      )
      .then((response) => {
        const updated = (
          response as unknown as { items: ShipmentDto[] }
        ).items.find((shipment) => shipment.id === shipmentId);
        if (updated) setSelectedShipment(updated);
      })
      .catch(() => undefined);
  };

  // #39990: shipments are edited in another browser tab (Edit / New), so
  // reload the list and the selected shipment's status on return.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      shipmentsGridRef.current?.api?.refreshServerSide();
      if (selectedShipment?.id) refreshSelectedShipment(selectedShipment.id);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  });

  const submit = async (input: LinkAnimalsToShipmentInput) => {
    try {
      const result = await linkAnimals.mutateAsync(input);
      if (
        result.requiresOverride &&
        !input.overrideExistingShipment &&
        result.conflicts.length > 0
      ) {
        setPendingOverride({ input, conflicts: result.conflicts });
        return;
      }
      setPendingOverride(null);
      toast.show({
        severity: result.success ? 'success' : 'error',
        summary: result.success
          ? t('common.success', 'Success')
          : t('common.error', 'Error'),
        detail: result.success
          ? (result.message ?? '')
          : (result.message ??
            t(
              'animalManagement.shipping.addFailed',
              'Failed to add animals to shipment',
            )),
        life: result.success ? undefined : 8000,
      });
      if (result.success) {
        removeFromCart(new Set(input.animalIds));
        shipmentsGridRef.current?.api?.refreshServerSide({ purge: true });
        refreshSelectedShipment(input.shipmentId);
        onAnimalsLinked();
      }
    } catch {
      setPendingOverride(null);
      toast.show({
        severity: 'error',
        summary: t('common.error', 'Error'),
        detail: t(
          'animalManagement.shipping.addFailed',
          'Failed to add animals to shipment',
        ),
      });
    }
  };

  const handleAddToShipment = () => {
    if (!selectedShipment?.id) return;
    void submit({
      shipmentId: selectedShipment.id,
      animalIds: checkedAnimals.map((animal) => animal.id),
    });
  };

  const handleClear = () => {
    removeFromCart(new Set(selectedAnimals.map((a) => a.id)));
    cartGridRef.current?.api?.deselectAll();
  };

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto xl:grid-cols-3">
      <div className="flex min-h-0 flex-col gap-4 xl:col-span-2">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">
              {t('animalManagement.shipping.selectedAnimals', {
                defaultValue: 'Selected Animals for Shipping ({count})',
                count: selectedAnimals.length,
              })}
            </h3>
            <div className="flex flex-wrap gap-2">
              <Button
                size="lg"
                variant="outline"
                onClick={handleClear}
                disabled={!selectedAnimals.length}
              >
                <XIcon className="mr-1.5 size-4" />
                {t('common.clear', 'Clear')}
              </Button>
              <span
                title={
                  isLocked
                    ? t(
                        'animalManagement.shipping.locked',
                        'Animals cannot be added to a shipment that is In Progress, a Transfer Trip, or Shipped.',
                      )
                    : isCarrierBlocked
                      ? t(
                          'animalManagement.shipping.parcelCarrier',
                          'UPS and FedEx cannot ship animals. Change the shipping method to add animals.',
                        )
                      : !selectedShipment?.id
                        ? t(
                            'animalManagement.shipping.selectShipment',
                            'Select a shipment below.',
                          )
                        : !checkedAnimals.length
                          ? t(
                              'animalManagement.shipping.checkAnimals',
                              'Check the animals to add.',
                            )
                          : undefined
                }
              >
                <Button
                  size="lg"
                  onClick={handleAddToShipment}
                  disabled={
                    !checkedAnimals.length ||
                    !selectedShipment?.id ||
                    isLocked ||
                    isCarrierBlocked ||
                    linkAnimals.isPending
                  }
                >
                  <ArrowRightIcon className="mr-1.5 size-4" />
                  {t(
                    'animalManagement.addToShipment.action',
                    'Add to Shipment',
                  )}
                </Button>
              </span>
            </div>
          </div>
          <div className="h-[250px] min-h-0">
            <WithLocalStorage storageKey="animal-management-shipping-cart-grid">
              <AgGridReact
                gridOptions={defaultGridOptions}
                ref={cartGridRef}
                className={cn(
                  'h-full w-full',
                  themeMode === 'dark'
                    ? 'ag-theme-transova-dark'
                    : 'ag-theme-transova',
                )}
                columnDefs={columns as AgGridReactProps['columnDefs']}
                defaultColDef={defaultColDef}
                rowData={selectedAnimals}
                rowModelType="clientSide"
                rowSelection="multiple"
                suppressRowClickSelection
                getRowId={getRowId<AnimalRow>((params) => params.data.id)}
                getContextMenuItems={getContextMenuItems}
                onSelectionChanged={(event: SelectionChangedEvent) =>
                  setCheckedAnimals(event.api.getSelectedRows())
                }
              />
            </WithLocalStorage>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold">
              {t('shipping.shipments', 'Shipments')}
            </h3>
            <div className="flex flex-wrap gap-2">
              <form
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  setShipmentSearchQuery(shipmentSearchInput.trim());
                }}
              >
                <Input
                  value={shipmentSearchInput}
                  onChange={(event) =>
                    setShipmentSearchInput(event.target.value)
                  }
                  placeholder={t('common.search', 'Search')}
                  className="h-9 w-56"
                />
                <Button
                  type="submit"
                  size="icon-lg"
                  variant="outline"
                  aria-label={t('common.search', 'Search')}
                >
                  <SearchIcon className="size-4" />
                </Button>
              </form>
              <Button
                size="lg"
                variant="outline"
                disabled={!selectedShipment?.id}
                onClick={() =>
                  window.open(
                    `/shipping/shipment?id=${selectedShipment?.id}`,
                    '_blank',
                  )
                }
              >
                <PencilIcon className="mr-1.5 size-4" />
                {t('common.edit', 'Edit')}
              </Button>
              <Button
                size="lg"
                onClick={() => window.open('/create-shipment', '_blank')}
              >
                <PlusIcon className="mr-1.5 size-4" />
                {t('common.new', 'New')}
              </Button>
            </div>
          </div>
          <ShipmentsGrid
            gridRef={shipmentsGridRef}
            selectedShipment={selectedShipment as never}
            setSelectedShipment={(shipment) =>
              setSelectedShipment(shipment as ShipmentDto | null)
            }
            filters={shipmentSearchQuery ? { search: shipmentSearchQuery } : {}}
            storageKey="animal-management-shipments-grid"
            showHeader={false}
          />
        </div>
      </div>

      <div className="xl:col-span-1">
        <ShipmentDetailsPanel
          shipment={selectedShipment}
          isLocked={isLocked}
          isCarrierBlocked={isCarrierBlocked}
        />
      </div>

      <AnimalShipmentOverrideDialog
        conflicts={pendingOverride?.conflicts ?? null}
        isSubmitting={linkAnimals.isPending}
        onCancel={() => setPendingOverride(null)}
        onConfirm={() => {
          if (!pendingOverride) return;
          void submit({
            ...pendingOverride.input,
            overrideExistingShipment: true,
          });
        }}
      />
    </div>
  );
}
